import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SportChips from "../../components/SportChips";
import SportTypeSelect from "../../components/SportTypeSelect";
import AgeGroupSelect from "../../components/AgeGroupSelect";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getClubById, getClubMembers, addClubMember, removeClubMember, updateClub, restartClubDemo, activateClub, deactivateClub, updateTeam, type Club, type ClubMember } from "../../services/clubs";
import { generateInvoiceForClub } from "../../services/invoices";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { formatDate } from "../../lib/dateFormat";
import { invoiceDocumentNumber, invoiceDocumentLabel, isFiscalInvoice } from "../../lib/invoiceDocument";

type TabType = "general" | "teams" | "admins" | "coaches" | "athletes" | "invoices" | "logs";

type TeamData = {
  id: string;
  name: string;
  age_group: string | null;
  sport_type: string | null;
  coach_id: string | null;
  coach?: {
    id: string;
    full_name: string | null;
    email: string;
  } | null;
  _count?: {
    athlete_profiles: number;
  };
};

export default function ClubDetails() {
  const { t } = useTranslation();
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const { activeRole } = useAuth();
  const isPlatformAdmin = activeRole === "platform_admin";

  const [activeTab, setActiveTab] = useState<TabType>("general");
  const [club, setClub] = useState<Club | null>(null);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [subscription, setSubscription] = useState<any>(null);
  const [billingTimeline, setBillingTimeline] = useState<{
    month: string;
    daysInMonth: number;
    costPerAthleteCents: number;
    costPerAthletePerDayCents: number;
    totalAthletes: number;
    totalUpToTodayCents: number;
    projectedMonthTotalCents: number;
    timeline: Array<{
      day: number;
      date: string;
      athleteCount: number;
      dailyAmount: number;
      cumulativeAmount: number;
      joinEvents: Array<{ athleteId: string; name: string; email: string }>;
      isPast: boolean;
      isToday: boolean;
    }>;
  } | null>(null);
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Club>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState<"club_admin" | "coach" | "athlete">("athlete");
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingGalleryPhoto, setIsUploadingGalleryPhoto] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [showInvoiceResultModal, setShowInvoiceResultModal] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState<{
    invoice: any;
    payment_link?: string;
    message: string;
  } | null>(null);

  // Teams state
  const [teams, setTeams] = useState<TeamData[]>([]);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [newTeamData, setNewTeamData] = useState({
    name: "",
    age_group: [] as string[],
    sport_type: [] as string[],
    coach_id: "",
  });
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);

  // Edit team modal state
  const [editingTeam, setEditingTeam] = useState<TeamData | null>(null);
  const [editTeamData, setEditTeamData] = useState({ name: "", age_group: "", sport_type: "" });
  const [isSavingTeam, setIsSavingTeam] = useState(false);

  useEffect(() => {
    if (clubId) {
      fetchClubData();
      fetchMembers(); // Fetch members on initial load to show tab counts
    }
  }, [clubId]);

  useEffect(() => {
    if (clubId && activeTab === "teams") {
      fetchTeams();
    } else if (clubId && (activeTab === "admins" || activeTab === "coaches" || activeTab === "athletes")) {
      fetchMembers();
    } else if (clubId && activeTab === "invoices") {
      fetchInvoices();
    } else if (clubId && activeTab === "logs") {
      fetchActivityLogs();
    }
  }, [activeTab, clubId]);

  const fetchClubData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getClubById(clubId!);
      console.log("Club data received:", data);
      setClub(data);
    } catch (err: any) {
      console.error("Failed to fetch club:", err);
      console.error("Error response:", err.response?.data);
      const errorMessage = err.response?.data?.error || err.message || t("clubDetails.failed_load", "Nu s-au putut încărca detaliile clubului");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const data = await getClubMembers(clubId!);
      setMembers(data);
    } catch (err) {
      console.error("Failed to fetch members:", err);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await api.get(`/clubs/${clubId}/teams`);
      setTeams(response.data);
    } catch (err) {
      console.error("Failed to fetch teams:", err);
    }
  };

  const handleCreateTeam = async () => {
    if (!clubId || !newTeamData.name.trim()) {
      toast.error(t("clubDetails.team_name_required", "Numele echipei este obligatoriu"));
      return;
    }

    try {
      setIsCreatingTeam(true);
      await api.post(`/clubs/${clubId}/teams`, {
        name: newTeamData.name.trim(),
        age_group: newTeamData.age_group.length ? newTeamData.age_group.join(", ") : null,
        sport_type: newTeamData.sport_type.length ? newTeamData.sport_type.join(", ") : null,
        coach_id: newTeamData.coach_id || null,
      });
      setShowCreateTeamModal(false);
      setNewTeamData({ name: "", age_group: [], sport_type: [], coach_id: "" });
      fetchTeams();
      fetchClubData(); // Refresh team count
    } catch (err: any) {
      console.error("Failed to create team:", err);
      toast.error(err.response?.data?.error || t("clubDetails.failed_create_team", "Nu s-a putut crea echipa"));
    } finally {
      setIsCreatingTeam(false);
    }
  };

  const handleDeleteTeam = async (teamId: string, teamName: string) => {
    if (!window.confirm(t("clubDetails.confirm_delete_team", "Sigur doriți să ștergeți această echipă?"))) return;

    try {
      await api.delete(`/teams/${teamId}`);
      fetchTeams();
      fetchClubData(); // Refresh team count
    } catch (err: any) {
      console.error("Failed to delete team:", err);
      toast.error(err.response?.data?.error || t("clubDetails.failed_delete_team", "Nu s-a putut șterge echipa"));
    }
  };

  const openEditTeamModal = (team: TeamData) => {
    setEditingTeam(team);
    setEditTeamData({
      name: team.name || "",
      age_group: team.age_group || "",
      sport_type: team.sport_type || "",
    });
  };

  const handleSaveTeamEdit = async () => {
    if (!editingTeam) return;
    if (!editTeamData.name.trim()) {
      toast.error(t("clubDetails.team_name_required", "Numele echipei este obligatoriu"));
      return;
    }
    setIsSavingTeam(true);
    try {
      await updateTeam(editingTeam.id, {
        name: editTeamData.name.trim(),
        age_group: editTeamData.age_group.trim() || undefined,
        sport_type: editTeamData.sport_type.trim() || undefined,
      });
      toast.success(t("clubDetails.team_updated", "Echipă actualizată"));
      setEditingTeam(null);
      fetchTeams();
    } catch (err: any) {
      console.error("Failed to update team:", err);
      toast.error(err.response?.data?.error || t("clubDetails.failed_update_team", "Nu s-a putut actualiza echipa"));
    }
    setIsSavingTeam(false);
  };

  const fetchInvoices = async () => {
    try {
      const [invoicesResponse, subscriptionResponse, timelineResponse] = await Promise.all([
        api.get(`/clubs/${clubId}/invoices`),
        api.get(`/clubs/${clubId}/subscription`).catch(() => ({ data: null })),
        api.get(`/clubs/${clubId}/billing-timeline`).catch(() => ({ data: null }))
      ]);
      setInvoices(invoicesResponse.data);
      setSubscription(subscriptionResponse.data);
      setBillingTimeline(timelineResponse.data);
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const response = await api.get(`/clubs/${clubId}/activity-logs?limit=50`);
      setActivityLogs(response.data);
    } catch (err) {
      console.error("Failed to fetch activity logs:", err);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!clubId) return;

    // Show confirmation modal-style approach using toast
    const confirmed = window.confirm(t("clubDetails.confirm_generate_invoice", "Generați o factură pentru acest club? Aceasta va crea un link de plată Stripe."));
    if (!confirmed) return;

    try {
      setIsGeneratingInvoice(true);
      const result = await generateInvoiceForClub(clubId);

      // Store result and show modal
      setInvoiceResult({
        invoice: result.invoice,
        payment_link: result.payment_link,
        message: result.message || t("clubDetails.invoice_generated_success", "Factură generată cu succes!"),
      });
      setShowInvoiceResultModal(true);

      if (result.payment_link) {
        navigator.clipboard.writeText(result.payment_link);
        toast.success(t("clubDetails.invoice_generated_link", "Factură generată! Linkul de plată a fost copiat în clipboard."));
      } else {
        toast.success(t("clubDetails.invoice_generated", "Factură generată!"));
      }

      // Refresh invoices
      fetchInvoices();
    } catch (err: any) {
      toast.error(err.response?.data?.error || t("clubDetails.failed_generate_invoice", "Nu s-a putut genera factura"));
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleDownloadInvoicePdf = async (invoiceId: string) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: "blob",
      });

      // Create download link
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err.response?.data?.error || t("clubDetails.failed_download_pdf", "Nu s-a putut descărca PDF-ul"));
    }
  };

  const handleRestartDemo = async () => {
    if (!club) return;
    if (!window.confirm(t("clubDetails.confirm_restart_demo", "Reluați perioada demo de 14 zile pentru administratorul acestui club? Aceasta va reseta numărătoarea demo la 14 zile de acum."))) {
      return;
    }
    try {
      const result = await restartClubDemo(club.id);
      const expiresLabel = formatDate(new Date(result.demo_expires_at));
      toast.success(t("clubDetails.demo_restarted", `Demo reluat. Expiră ${expiresLabel}.`));
      fetchClubData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || t("clubDetails.failed_restart_demo", "Nu s-a putut relua demo-ul"));
    }
  };

  /**
   * Activation asks which plan, because that is the whole decision.
   *
   * The button used to flip the admin accounts to "active" and stop — which
   * left the club on `demo`, still trial-gated, still billed nothing. The only
   * way to really activate a club was to open the edit modal and change the
   * plan there. So: pick the plan here, and the server sets the plan, the
   * price, the metering anchor and the accounts together.
   */
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activatePlan, setActivatePlan] = useState<"basic" | "premium">("basic");
  const [activating, setActivating] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  /** On a paid plan already — the only sensible next step is turning it off. */
  const clubIsPaid = !!club && (club.plan_code === "basic" || club.plan_code === "premium");

  const handleActivateClub = async () => {
    if (!club) return;
    setActivating(true);
    try {
      const result = await activateClub(club.id, activatePlan);
      toast.success(
        t(
          "clubDetails.club_activated",
          `Club activat pe planul ${result.plan_name || activatePlan}. Contorizarea a pornit.`
        )
      );
      setActivateModalOpen(false);
      fetchClubData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || t("clubDetails.failed_activate", "Nu s-a putut activa clubul"));
    }
    setActivating(false);
  };

  const handleDeactivateClub = async () => {
    if (!club) return;
    if (
      !window.confirm(
        t(
          "clubDetails.confirm_deactivate",
          "Dezactivați acest club? Clubul nu va mai fi facturat, iar administratorii nu se vor mai putea autentifica până la o nouă activare. Nu se șterge nimic — datele, echipele și istoricul rămân."
        )
      )
    ) {
      return;
    }
    setDeactivating(true);
    try {
      await deactivateClub(club.id);
      toast.success(t("clubDetails.club_deactivated", "Club dezactivat. Nu s-a șters nimic."));
      fetchClubData();
    } catch (err: any) {
      toast.error(
        err.response?.data?.error || t("clubDetails.failed_deactivate", "Nu s-a putut dezactiva clubul")
      );
    }
    setDeactivating(false);
  };

  const handleEditClick = () => {
    if (club) {
      setEditFormData({
        name: club.name,
        legal_name: club.legal_name || "",
        cui: club.cui || "",
        cis_code: club.cis_code || "",
        registration_number: club.registration_number || "",
        business_address: club.business_address || "",
        bank_account: club.bank_account || "",
        bank_name: club.bank_name || "",
        public_name: club.public_name || "",
        physical_address: club.physical_address || "",
        city: club.city || "",
        county: club.county || "",
        country: club.country || "Romania",
        website: club.website || "",
        description: club.description || "",
        logo_url: club.logo_url || "",
        sports: club.sports || [],
        plan_code: club.plan_code,
        status: club.status,
        cost_per_athlete_cents: club.cost_per_athlete_cents,
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({});
  };

  const handleSaveEdit = async () => {
    try {
      setIsSaving(true);
      const { data } = await api.patch(`/clubs/${clubId}`, editFormData);
      setClub(data);
      setIsEditing(false);
      setEditFormData({});
      toast.success(t("clubDetails.club_updated", "Detaliile clubului au fost actualizate cu succes!"));
    } catch (err: any) {
      console.error("Failed to update club:", err);
      toast.error(err.response?.data?.error || t("clubDetails.error_saving", "Eroare la salvarea datelor"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof Club, value: any) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSportsChange = (sports: string[]) => {
    setEditFormData(prev => ({ ...prev, sports }));
  };

  const handleAddMemberClick = () => {
    setShowAddMemberModal(true);
    setNewMemberEmail("");
    setNewMemberRole("athlete");
  };

  const handleAddMember = async () => {
    try {
      if (!newMemberEmail.trim()) {
        toast.error(t("clubDetails.enter_email", "Introduceți emailul utilizatorului!"));
        return;
      }

      setIsAddingMember(true);

      // Search for user by email
      const { data: usersData } = await api.get(`/users?q=${encodeURIComponent(newMemberEmail)}`);

      if (!usersData.data || usersData.data.length === 0) {
        toast.error(t("clubDetails.user_not_found", "Utilizator nu a fost găsit! Verificați emailul."));
        setIsAddingMember(false);
        return;
      }

      const user = usersData.data[0];

      // Add user to club with selected role
      await addClubMember(clubId!, {
        user_id: user.id,
        role_code: newMemberRole,
      });

      toast.success(t("clubDetails.member_added", "Membru adăugat cu succes!"));
      setShowAddMemberModal(false);
      setNewMemberEmail("");
      setNewMemberRole("athlete");

      // Refresh members list
      fetchMembers();
      fetchClubData();
    } catch (err: any) {
      console.error("Failed to add member:", err);
      toast.error(err.response?.data?.error || t("clubDetails.error_adding_member", "Eroare la adăugarea membrului"));
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId: string, userName: string) => {
    if (!window.confirm(t("clubDetails.confirm_remove_member", "Sigur doriți să eliminați acest membru din club?"))) {
      return;
    }

    try {
      await removeClubMember(clubId!, userId);
      toast.success(t("clubDetails.member_removed", "Membru eliminat cu succes!"));

      // Refresh members list
      fetchMembers();
      fetchClubData();
    } catch (err: any) {
      console.error("Failed to remove member:", err);
      toast.error(err.response?.data?.error || t("clubDetails.error_removing_member", "Eroare la eliminarea membrului"));
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error(t("clubDetails.select_image_file", "Vă rugăm să selectați un fișier imagine!"));
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("clubDetails.image_too_large", "Imaginea este prea mare! Dimensiunea maximă este 10MB."));
      return;
    }

    try {
      setIsUploadingLogo(true);

      // Upload image
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await api.post("/upload/club-logo", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Update club with new logo URL
      const updateResponse = await api.patch(`/clubs/${clubId}`, {
        logo_url: data.url,
      });

      setClub(updateResponse.data);
      toast.success(t("clubDetails.logo_updated", "Logo-ul clubului a fost actualizat cu succes!"));
    } catch (err: any) {
      console.error("Failed to upload logo:", err);
      toast.error(err.response?.data?.error || t("clubDetails.error_uploading_logo", "Eroare la încărcarea logo-ului"));
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleGalleryPhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error(t("clubDetails.select_image_file", "Vă rugăm să selectați un fișier imagine!"));
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("clubDetails.image_too_large", "Imaginea este prea mare! Dimensiunea maximă este 10MB."));
      return;
    }

    try {
      setIsUploadingGalleryPhoto(true);

      // Upload image
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await api.post("/upload/image?folder=club-gallery", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Add photo to gallery
      const currentGallery = club?.gallery || [];
      const updatedGallery = [...currentGallery, data.url];

      const updateResponse = await api.patch(`/clubs/${clubId}`, {
        gallery: updatedGallery,
      });

      setClub(updateResponse.data);
      toast.success(t("clubDetails.photo_added", "Fotografia a fost adăugată în galerie!"));

      // Reset file input
      event.target.value = "";
    } catch (err: any) {
      console.error("Failed to upload gallery photo:", err);
      toast.error(err.response?.data?.error || t("clubDetails.error_uploading_photo", "Eroare la încărcarea fotografiei"));
    } finally {
      setIsUploadingGalleryPhoto(false);
    }
  };

  const handleDeleteGalleryPhoto = async (photoUrl: string) => {
    if (!window.confirm(t("clubDetails.confirm_delete_photo", "Sigur doriți să ștergeți această fotografie din galerie?"))) {
      return;
    }

    try {
      // Remove photo from gallery
      const currentGallery = club?.gallery || [];
      const updatedGallery = currentGallery.filter(url => url !== photoUrl);

      const updateResponse = await api.patch(`/clubs/${clubId}`, {
        gallery: updatedGallery,
      });

      setClub(updateResponse.data);
      toast.success(t("clubDetails.photo_deleted", "Fotografia a fost ștearsă din galerie!"));
    } catch (err: any) {
      console.error("Failed to delete gallery photo:", err);
      toast.error(err.response?.data?.error || t("clubDetails.error_deleting_photo", "Eroare la ștergerea fotografiei"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-slate-500">{t("common.loading")}</span>
        </div>
      </div>
    );
  }

  if (error || !club) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-600">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error || t("clubDetails.club_not_found", "Clubul nu a fost găsit")}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const clubAdmins = members.filter(m => m.role.code === "club_admin");
  const athletes = members.filter(m => m.role.code === "athlete");
  const coaches = members.filter(m => m.role.code === "coach");

  const tabs = [
    { key: "general" as const, label: t("clubDetails.tab_general", "Informații generale"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )},
    { key: "teams" as const, label: t("clubDetails.tab_teams", "Echipe"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ), count: teams.length || club?.team_count || 0 },
    { key: "admins" as const, label: t("clubDetails.tab_admins", "Administratori club"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ), count: clubAdmins.length },
    { key: "coaches" as const, label: t("clubDetails.tab_coaches", "Antrenori"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
      </svg>
    ), count: coaches.length },
    { key: "athletes" as const, label: t("clubDetails.tab_athletes", "Sportivi"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ), count: athletes.length },
    { key: "invoices" as const, label: t("clubDetails.tab_invoices", "Facturi"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
      </svg>
    )},
    { key: "logs" as const, label: t("clubDetails.tab_logs", "Jurnal de activitate"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    )},
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-6 relative">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/clubs")}
                className="p-2 bg-white hover:bg-slate-100 text-slate-600 rounded-xl border border-slate-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-teal-50 rounded-xl">
                  <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-800">
                    {club.public_name || club.name}
                  </h1>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      club.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
                    }`}>
                      {club.status}
                    </span>
                    {club.description && (
                      <span className="text-slate-500 text-sm">{club.description}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {!isEditing ? (
              <div className="flex flex-wrap gap-2">
                {/* A club on a paid plan has nothing to restart and nothing to
                    activate; the one remaining action is turning it off. */}
                {!clubIsPaid && (
                  <>
                    <button
                      onClick={handleRestartDemo}
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
                      title={t("clubDetails.restart_demo_tooltip", "Resetează perioada demo de 14 zile pentru acest club")}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {t("clubDetails.restart_demo", "Reia demo (14 zile)")}
                    </button>
                    <button
                      onClick={() => {
                        setActivatePlan("basic");
                        setActivateModalOpen(true);
                      }}
                      className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
                      title={t("clubDetails.activate_club_tooltip", "Pune clubul pe un plan plătit și încheie perioada demo")}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t("clubDetails.activate_club", "Activează club")}
                    </button>
                  </>
                )}
                {clubIsPaid && (
                  <button
                    onClick={handleDeactivateClub}
                    disabled={deactivating}
                    className="px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2 disabled:opacity-60"
                    title={t("clubDetails.deactivate_club_tooltip", "Scoate clubul de pe planul plătit. Nu se șterge nimic.")}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    {deactivating
                      ? t("common.processing", "Se procesează...")
                      : t("clubDetails.deactivate_club", "Dezactivează club")}
                  </button>
                )}
                <button
                  onClick={handleEditClick}
                  className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t("clubDetails.edit_club", "Editează clubul")}
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors disabled:opacity-50"
                >
                  {t("clubDetails.cancel", "Anulează")}
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t("clubDetails.saving", "Se salvează...")}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t("clubDetails.save_changes", "Salvează modificările")}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:border-teal-300 transition-colors">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-xl">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">{t("clubDetails.total_members", "Total membri")}</p>
                <p className="text-2xl font-bold text-slate-800">{club.member_count || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:border-teal-300 transition-colors">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-50 rounded-xl">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">{t("clubDetails.teams", "Echipe")}</p>
                <p className="text-2xl font-bold text-slate-800">{club.team_count || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:border-teal-300 transition-colors">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-50 rounded-xl">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">{t("clubDetails.sports", "Sporturi")}</p>
                <p className="text-2xl font-bold text-slate-800">{Array.isArray(club.sports) ? club.sports.length : 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:border-teal-300 transition-colors">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-50 rounded-xl">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">{t("clubDetails.plan", "Plan")}</p>
                <p className="text-2xl font-bold text-slate-800">{club.plan_code}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Billing Rate Card - Platform Admin Only */}
        {isPlatformAdmin && (
          <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl shadow-lg p-6 mb-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white/80 text-sm font-medium">{t("clubDetails.billing_rate", "Tarif facturare")}</p>
                  <p className="text-3xl font-bold">€{((club.cost_per_athlete_cents || 100) / 100).toFixed(2)}<span className="text-lg font-normal text-white/70"> / {t("clubDetails.athlete", "sportiv")} / {t("clubDetails.month", "lună")}</span></p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-sm font-medium">{t("clubDetails.estimated_monthly_revenue", "Venit lunar estimat")}</p>
                <p className="text-3xl font-bold">€{(((club.athlete_count || 0) * (club.cost_per_athlete_cents || 100)) / 100).toFixed(2)}</p>
                <p className="text-white/60 text-sm mt-1">{club.athlete_count || 0} {t("clubDetails.athletes_lower", "sportivi")} × €{((club.cost_per_athlete_cents || 100) / 100).toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex border-b border-slate-200 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.key
                    ? "border-b-2 border-teal-500 text-teal-600 bg-teal-50/50"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    activeTab === tab.key ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-600"
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "general" && (
              <div className="space-y-6">
                {/* Club Logo Section */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-teal-50 rounded-lg">
                      <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.club_logo", "Logo club")}</h2>
                  </div>
                  <div className="flex items-center gap-6">
                    {/* Current Logo Display */}
                    <div className="flex-shrink-0">
                      {club.logo_url ? (
                        <img
                          src={club.logo_url}
                          alt={club.name}
                          className="w-32 h-32 object-contain bg-white rounded-xl border border-slate-200"
                        />
                      ) : (
                        <div className="w-32 h-32 bg-white rounded-xl border border-slate-200 flex items-center justify-center">
                          <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Upload Button */}
                    <div className="flex-1">
                      <p className="text-sm text-slate-600 mb-3">
                        {t("clubDetails.upload_logo_hint", "Încărcați logo-ul clubului (PNG recomandat pentru transparență)")}
                      </p>
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-xl cursor-pointer transition-colors disabled:opacity-50">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          disabled={isUploadingLogo}
                          className="hidden"
                        />
                        {isUploadingLogo ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>{t("clubDetails.uploading", "Se încarcă...")}</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            <span>{t("clubDetails.upload_logo", "Încarcă logo")}</span>
                          </>
                        )}
                      </label>
                      <p className="text-xs text-slate-500 mt-2">
                        {t("clubDetails.logo_size_hint", "Dimensiune recomandată: 400x400px • Format: PNG, JPG • Max: 10MB")}
                      </p>
                    </div>
                  </div>
                </div>

                {!isEditing ? (
                  <>
                    {/* Billing Rate Card - View Mode (Platform Admin Only) */}
                    {isPlatformAdmin && (
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-amber-100 rounded-xl">
                              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm text-amber-600 font-medium">{t("clubDetails.billing_rate", "Tarif facturare")}</p>
                              <p className="text-3xl font-bold text-amber-800">
                                €{((club.cost_per_athlete_cents || 100) / 100).toFixed(2)}
                                <span className="text-base font-normal text-amber-600 ml-1">/{t("clubDetails.athlete", "sportiv")}/{t("clubDetails.month", "lună")}</span>
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-amber-600 mb-1">{t("clubDetails.estimated_monthly_revenue", "Venit lunar estimat")}</p>
                            <p className="text-2xl font-bold text-amber-700">
                              €{(((club.athlete_count || 0) * (club.cost_per_athlete_cents || 100)) / 100).toFixed(2)}
                            </p>
                            <p className="text-xs text-amber-500">
                              {club.athlete_count || 0} {t("clubDetails.athletes_lower", "sportivi")} × €{((club.cost_per_athlete_cents || 100) / 100).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Business Information - View Mode */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.business_information", "Informații de business")}</h2>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.legal_name", "Denumire legală")}</p>
                          <p className="text-sm font-medium text-slate-800">{club.legal_name || "-"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.cui", "CUI (Cod fiscal)")}</p>
                          <p className="text-sm font-medium text-slate-800">{club.cui || "-"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.cis_code", "Cod CIS")}</p>
                          <p className="text-sm font-medium text-slate-800">{club.cis_code || "-"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.registration_number", "Număr de înregistrare")}</p>
                          <p className="text-sm font-medium text-slate-800">{club.registration_number || "-"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.business_address", "Adresă sediu")}</p>
                          <p className="text-sm font-medium text-slate-800">{club.business_address || "-"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.bank_account", "Cont bancar")}</p>
                          <p className="text-sm font-medium text-slate-800 font-mono">{club.bank_account || "-"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.bank_name", "Numele băncii")}</p>
                          <p className="text-sm font-medium text-slate-800">{club.bank_name || "-"}</p>
                        </div>
                      </div>
                    </div>

                    {/* Public Information - View Mode */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-emerald-50 rounded-lg">
                          <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                          </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.public_information", "Informații publice")}</h2>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.public_name", "Nume public")}</p>
                          <p className="text-sm font-medium text-slate-800">{club.public_name || club.name}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.physical_address", "Adresă fizică")}</p>
                          <p className="text-sm font-medium text-slate-800">{club.physical_address || "-"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.city", "Oraș")}</p>
                          <p className="text-sm font-medium text-slate-800">{club.city || "-"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.county", "Județ")}</p>
                          <p className="text-sm font-medium text-slate-800">{club.county || "-"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.country", "Țară")}</p>
                          <p className="text-sm font-medium text-slate-800">{club.country || "Romania"}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">{t("clubDetails.website", "Site web")}</p>
                          {club.website ? (
                            <a
                              href={club.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-teal-600 hover:underline font-medium"
                            >
                              {club.website}
                            </a>
                          ) : (
                            <p className="text-sm font-medium text-slate-800">-</p>
                          )}
                        </div>
                      </div>

                      {/* Social Media */}
                      {club.social_media && typeof club.social_media === "object" && (
                        <div className="mt-4">
                          <p className="text-xs text-slate-500 mb-2">{t("clubDetails.social_media", "Rețele sociale")}</p>
                          <div className="flex gap-2 flex-wrap">
                            {Object.entries(club.social_media).map(([platform, url]) => (
                              <a
                                key={platform}
                                href={url as string}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 bg-white hover:bg-slate-50 text-teal-600 border border-slate-200 rounded-lg text-sm transition-colors capitalize"
                              >
                                {platform}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Sports */}
                      {club.sports && Array.isArray(club.sports) && club.sports.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs text-slate-500 mb-2">{t("clubDetails.sports_activities", "Sporturi și activități")}</p>
                          <SportChips
                            values={club.sports}
                            className="gap-2"
                            chipClassName="px-3 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-sm font-medium"
                          />
                        </div>
                      )}
                    </div>

                    {/* Photo Gallery Section */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-purple-50 rounded-lg">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.photo_gallery", "Galerie foto")}</h2>
                      </div>

                      {/* Upload Button */}
                      <div className="mb-4">
                        <label className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-xl cursor-pointer transition-colors disabled:opacity-50">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleGalleryPhotoUpload}
                            disabled={isUploadingGalleryPhoto}
                            className="hidden"
                          />
                          {isUploadingGalleryPhoto ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>{t("clubDetails.uploading", "Se încarcă...")}</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              <span>{t("clubDetails.add_photo", "Adaugă fotografie")}</span>
                            </>
                          )}
                        </label>
                        <p className="text-xs text-slate-500 mt-2">
                          {t("clubDetails.gallery_hint", "Adăugați fotografii în galeria clubului • Format: JPG, PNG • Max: 10MB")}
                        </p>
                      </div>

                      {/* Gallery Grid */}
                      {club.gallery && Array.isArray(club.gallery) && club.gallery.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {club.gallery.map((photoUrl, index) => (
                            <div
                              key={index}
                              className="relative group bg-white rounded-xl border border-slate-200 overflow-hidden aspect-square"
                            >
                              <img
                                src={photoUrl}
                                alt={`${t("clubDetails.gallery_photo", "Fotografie galerie")} ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              {/* Delete Button - visible on hover */}
                              <button
                                onClick={() => handleDeleteGalleryPhoto(photoUrl)}
                                className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                          <div className="p-3 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="text-slate-500">{t("clubDetails.no_photos", "Nicio fotografie în galerie")}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {t("clubDetails.no_photos_hint", "Apăsați butonul \"Adaugă fotografie\" pentru a adăuga fotografii")}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Business Information - Edit Mode */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.business_information", "Informații de business")}</h2>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.legal_name", "Denumire legală")}</label>
                          <input
                            type="text"
                            value={editFormData.legal_name || ""}
                            onChange={(e) => handleInputChange("legal_name", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.cui", "CUI (Cod fiscal)")}</label>
                          <input
                            type="text"
                            value={editFormData.cui || ""}
                            onChange={(e) => handleInputChange("cui", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.cis_code", "Cod CIS")}</label>
                          <input
                            type="text"
                            value={editFormData.cis_code || ""}
                            onChange={(e) => handleInputChange("cis_code", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.registration_number", "Număr de înregistrare")}</label>
                          <input
                            type="text"
                            value={editFormData.registration_number || ""}
                            onChange={(e) => handleInputChange("registration_number", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.business_address", "Adresă sediu")}</label>
                          <input
                            type="text"
                            value={editFormData.business_address || ""}
                            onChange={(e) => handleInputChange("business_address", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.bank_account_iban", "Cont bancar (IBAN)")}</label>
                          <input
                            type="text"
                            value={editFormData.bank_account || ""}
                            onChange={(e) => handleInputChange("bank_account", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 font-mono focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.bank_name", "Numele băncii")}</label>
                          <input
                            type="text"
                            value={editFormData.bank_name || ""}
                            onChange={(e) => handleInputChange("bank_name", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Billing Settings - Platform Admin Only */}
                    {isPlatformAdmin && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 bg-amber-100 rounded-lg">
                            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.billing_settings", "Setări facturare")}</h2>
                            <p className="text-xs text-amber-600">{t("clubDetails.platform_admin_only", "Doar administrator platformă")}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              {t("clubDetails.cost_per_athlete", "Cost pe sportiv (EUR/lună)")}
                            </label>
                            <div className="relative">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">€</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={((editFormData.cost_per_athlete_cents || 100) / 100).toFixed(2)}
                                onChange={(e) => {
                                  const euros = parseFloat(e.target.value) || 0;
                                  handleInputChange("cost_per_athlete_cents", Math.round(euros * 100));
                                }}
                                className="w-full pl-8 pr-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                                placeholder="1.00"
                              />
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {t("clubDetails.cost_per_athlete_hint", "Acesta este costul lunar perceput per sportiv activ din acest club.")}
                            </p>
                          </div>
                          <div className="flex items-end">
                            <div className="bg-white border border-amber-200 rounded-xl p-4 w-full">
                              <p className="text-sm text-slate-600">{t("clubDetails.current_athletes", "Sportivi curenți")}</p>
                              <p className="text-2xl font-bold text-amber-700">{club?.athlete_count || 0}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                {t("clubDetails.estimated_monthly", "Estimare lunară")}: €{(((club?.athlete_count || 0) * (editFormData.cost_per_athlete_cents || 100)) / 100).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Public Information - Edit Mode */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-emerald-50 rounded-lg">
                          <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                          </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.public_information", "Informații publice")}</h2>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.public_name", "Nume public")}</label>
                          <input
                            type="text"
                            value={editFormData.public_name || ""}
                            onChange={(e) => handleInputChange("public_name", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.physical_address", "Adresă fizică")}</label>
                          <input
                            type="text"
                            value={editFormData.physical_address || ""}
                            onChange={(e) => handleInputChange("physical_address", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.city", "Oraș")}</label>
                          <input
                            type="text"
                            value={editFormData.city || ""}
                            onChange={(e) => handleInputChange("city", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.county", "Județ")}</label>
                          <input
                            type="text"
                            value={editFormData.county || ""}
                            onChange={(e) => handleInputChange("county", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.country", "Țară")}</label>
                          <input
                            type="text"
                            value={editFormData.country || "Romania"}
                            onChange={(e) => handleInputChange("country", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.website", "Site web")}</label>
                          <input
                            type="url"
                            value={editFormData.website || ""}
                            onChange={(e) => handleInputChange("website", e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                            placeholder="https://example.com"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.description", "Descriere")}</label>
                          <textarea
                            value={editFormData.description || ""}
                            onChange={(e) => handleInputChange("description", e.target.value)}
                            rows={3}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all resize-none"
                            placeholder={t("clubDetails.describe_club_placeholder", "Descrieți clubul...")}
                          />
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.sports_comma_separated", "Sporturi (separate prin virgulă)")}</label>
                          <input
                            type="text"
                            value={editFormData.sports?.join(", ") || ""}
                            onChange={(e) => handleSportsChange(e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                            className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                            placeholder={t("clubDetails.sports_placeholder", "Fotbal, Baschet, Volei...")}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Teams Tab */}
            {activeTab === "teams" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.teams", "Echipe")}</h2>
                  </div>
                  <button
                    onClick={() => setShowCreateTeamModal(true)}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t("clubDetails.create_team", "Creează echipă")}
                  </button>
                </div>

                {teams.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                    <svg className="w-12 h-12 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    <p className="text-slate-500">{t("clubDetails.no_teams", "Nu există echipe")}</p>
                    <p className="text-sm text-slate-400 mt-1">{t("clubDetails.no_teams_hint", "Creați prima echipă pentru a începe")}</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {teams.map((team) => (
                      <div
                        key={team.id}
                        className="p-5 bg-white rounded-xl border border-slate-200 hover:border-amber-300 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-slate-800">{team.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              {team.sport_type && (
                                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md text-xs font-medium">
                                  {team.sport_type}
                                </span>
                              )}
                              {team.age_group && (
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium">
                                  {team.age_group}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEditTeamModal(team)}
                              className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-colors"
                              title={t("clubDetails.edit_team", "Editează echipa")}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteTeam(team.id, team.name)}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title={t("clubDetails.delete_team", "Șterge echipa")}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-slate-600">
                            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>{t("clubDetails.coach", "Antrenor")}: {team.coach?.full_name || team.coach?.email || t("clubDetails.not_assigned", "Nealocat")}</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span>{team._count?.athlete_profiles || 0} {t("clubDetails.athletes_lower", "sportivi")}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Create Team Modal */}
                {showCreateTeamModal && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                      <div className="p-6 border-b border-slate-200">
                        <h3 className="text-lg font-semibold text-slate-800">{t("clubDetails.create_new_team", "Creează echipă nouă")}</h3>
                      </div>
                      <div className="p-6 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t("clubDetails.team_name", "Nume echipă")} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={newTeamData.name}
                            onChange={(e) => setNewTeamData({ ...newTeamData, name: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                            placeholder={t("clubDetails.team_name_placeholder", "ex. Fotbal băieți U-15")}
                          />
                        </div>
                        <AgeGroupSelect
                          value={newTeamData.age_group}
                          onChange={(vals) => setNewTeamData({ ...newTeamData, age_group: vals })}
                          label="Grupă de vârstă"
                          placeholder="Selectează grupe de vârstă..."
                        />
                        <SportTypeSelect
                          value={newTeamData.sport_type}
                          onChange={(vals) => setNewTeamData({ ...newTeamData, sport_type: vals })}
                          label="Tip sport"
                          placeholder="Selectează sporturi..."
                        />
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t("clubDetails.assign_coach_optional", "Alocă antrenor (opțional)")}
                          </label>
                          <select
                            value={newTeamData.coach_id}
                            onChange={(e) => setNewTeamData({ ...newTeamData, coach_id: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">{t("clubDetails.no_coach_assigned", "Niciun antrenor alocat")}</option>
                            {coaches.map((coach) => (
                              <option key={coach.user.id} value={coach.user.id}>
                                {coach.user.full_name || coach.user.email}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="p-6 border-t border-slate-200 flex gap-3">
                        <button
                          onClick={() => {
                            setShowCreateTeamModal(false);
                            setNewTeamData({ name: "", age_group: [], sport_type: [], coach_id: "" });
                          }}
                          className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                        >
                          {t("clubDetails.cancel", "Anulează")}
                        </button>
                        <button
                          onClick={handleCreateTeam}
                          disabled={isCreatingTeam || !newTeamData.name.trim()}
                          className="flex-1 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                        >
                          {isCreatingTeam ? t("clubDetails.creating", "Se creează...") : t("clubDetails.create_team", "Creează echipă")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Edit Team Modal */}
                {editingTeam && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                      <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-slate-800">{t("clubDetails.edit_team_title", "Editează echipa")}</h3>
                        <button
                          onClick={() => setEditingTeam(null)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="p-6 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t("clubDetails.team_name", "Nume echipă")} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={editTeamData.name}
                            onChange={(e) => setEditTeamData({ ...editTeamData, name: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t("clubDetails.age_group", "Grupă de vârstă")}
                          </label>
                          <input
                            type="text"
                            value={editTeamData.age_group}
                            onChange={(e) => setEditTeamData({ ...editTeamData, age_group: e.target.value })}
                            placeholder="ex. U-15"
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t("clubDetails.sport_type", "Sport")}
                          </label>
                          <input
                            type="text"
                            value={editTeamData.sport_type}
                            onChange={(e) => setEditTeamData({ ...editTeamData, sport_type: e.target.value })}
                            placeholder="ex. football"
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                      </div>
                      <div className="p-6 border-t border-slate-200 flex gap-3">
                        <button
                          onClick={() => setEditingTeam(null)}
                          disabled={isSavingTeam}
                          className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors disabled:opacity-50"
                        >
                          {t("clubDetails.cancel", "Anulează")}
                        </button>
                        <button
                          onClick={handleSaveTeamEdit}
                          disabled={isSavingTeam || !editTeamData.name.trim()}
                          className="flex-1 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                        >
                          {isSavingTeam ? t("clubDetails.saving", "Se salvează...") : t("clubDetails.save_changes", "Salvează")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "admins" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.club_administrators", "Administratori club")}</h2>
                  </div>
                  <button
                    onClick={() => { setNewMemberRole("club_admin"); handleAddMemberClick(); }}
                    className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t("clubDetails.add_admin", "Adaugă administrator")}
                  </button>
                </div>
                {clubAdmins.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                    <p className="text-slate-500">{t("clubDetails.no_admins", "Nu există administratori de club")}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {clubAdmins.map((admin) => (
                      <div
                        key={admin.user.id}
                        className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-teal-300 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <button
                              onClick={() => navigate(`/admin/users/${admin.user.id}`)}
                              className="font-medium text-teal-600 hover:text-teal-700 underline text-left"
                            >
                              {admin.user.full_name || admin.user.email}
                            </button>
                            <p className="text-sm text-slate-500">{admin.user.email}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {t("clubDetails.joined", "Înscris la")}: {formatDate(new Date(admin.joined_at))}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-3 py-1 bg-purple-50 text-purple-600 border border-purple-200 rounded-full text-xs font-medium">
                              {admin.role.label}
                            </span>
                            <button
                              onClick={() => handleRemoveMember(admin.user.id, admin.user.full_name || admin.user.email)}
                              className="px-3 py-1 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg text-xs transition-colors"
                            >
                              {t("clubDetails.remove", "Elimină")}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "coaches" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                      </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.coaches", "Antrenori")}</h2>
                  </div>
                  <button
                    onClick={() => { setNewMemberRole("coach"); handleAddMemberClick(); }}
                    className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t("clubDetails.add_coach", "Adaugă antrenor")}
                  </button>
                </div>
                {coaches.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                    <p className="text-slate-500">{t("clubDetails.no_coaches", "Nu există antrenori în acest club")}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.name", "Nume")}</th>
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.email", "Email")}</th>
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.joined", "Înscris la")}</th>
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.actions", "Acțiuni")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coaches.map((coach) => (
                          <tr key={coach.user.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                            <td className="py-3 px-4 text-sm">
                              <button
                                onClick={() => navigate(`/admin/users/${coach.user.id}`)}
                                className="text-teal-600 hover:text-teal-700 font-medium underline text-left"
                              >
                                {coach.user.full_name || coach.user.email}
                              </button>
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600">{coach.user.email}</td>
                            <td className="py-3 px-4 text-sm text-slate-600">
                              {formatDate(new Date(coach.joined_at))}
                            </td>
                            <td className="py-3 px-4">
                              <button
                                onClick={() => handleRemoveMember(coach.user.id, coach.user.full_name || coach.user.email)}
                                className="px-3 py-1 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg text-xs transition-colors"
                              >
                                Elimină
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === "athletes" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.athletes", "Sportivi")}</h2>
                  </div>
                  <button
                    onClick={() => { setNewMemberRole("athlete"); handleAddMemberClick(); }}
                    className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t("clubDetails.add_athlete", "Adaugă sportiv")}
                  </button>
                </div>
                {athletes.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                    <p className="text-slate-500">{t("clubDetails.no_athletes", "Nu există sportivi în acest club")}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.name", "Nume")}</th>
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.email", "Email")}</th>
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.joined", "Înscris la")}</th>
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.actions", "Acțiuni")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {athletes.map((athlete) => (
                          <tr key={athlete.user.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                            <td className="py-3 px-4 text-sm">
                              <button
                                onClick={() => navigate(`/admin/users/${athlete.user.id}`)}
                                className="text-teal-600 hover:text-teal-700 font-medium underline text-left"
                              >
                                {athlete.user.full_name || athlete.user.email}
                              </button>
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600">{athlete.user.email}</td>
                            <td className="py-3 px-4 text-sm text-slate-600">
                              {formatDate(new Date(athlete.joined_at))}
                            </td>
                            <td className="py-3 px-4">
                              <button
                                onClick={() => handleRemoveMember(athlete.user.id, athlete.user.full_name || athlete.user.email)}
                                className="px-3 py-1 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg text-xs transition-colors"
                              >
                                Elimină
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === "invoices" && (
              <div>
                {/* Billing Cycle Info */}
                {(() => {
                  // Get last paid invoice date
                  const paidInvoices = invoices.filter(inv => inv.paid_at);
                  const lastBillingDate = paidInvoices.length > 0
                    ? new Date(Math.max(...paidInvoices.map(inv => new Date(inv.paid_at).getTime())))
                    : null;

                  // Calculate next billing date (30 days from last billing or from registration)
                  const baseDate = lastBillingDate || (club?.created_at ? new Date(club.created_at) : new Date());
                  const nextBillingDate = new Date(baseDate);
                  nextBillingDate.setDate(nextBillingDate.getDate() + 30);

                  const today = new Date();
                  const daysUntilNextBilling = Math.ceil((nextBillingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                      {/* Registration Date */}
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-3 bg-white/80 rounded-xl shadow-sm">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm text-blue-600 font-medium">{t("clubDetails.club_registration", "Înregistrare club")}</p>
                            <p className="text-xl font-bold text-blue-800">
                              {club?.created_at
                                ? new Date(club.created_at).toLocaleDateString("ro-RO", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric"
                                  })
                                : "N/A"
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Last Billing Date */}
                      <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-6 border border-slate-200">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-3 bg-white/80 rounded-xl shadow-sm">
                            <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm text-slate-600 font-medium">{t("clubDetails.last_billing", "Ultima facturare")}</p>
                            <p className="text-xl font-bold text-slate-800">
                              {lastBillingDate
                                ? lastBillingDate.toLocaleDateString("ro-RO", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric"
                                  })
                                : t("clubDetails.never_billed", "Niciodată facturat")
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Next Billing Date (30-day cycle) */}
                      <div className={`rounded-2xl p-6 border ${
                        daysUntilNextBilling <= 7
                          ? "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"
                          : "bg-gradient-to-br from-teal-50 to-emerald-50 border-teal-200"
                      }`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-3 bg-white/80 rounded-xl shadow-sm">
                            <svg className={`w-6 h-6 ${daysUntilNextBilling <= 7 ? "text-amber-600" : "text-teal-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${daysUntilNextBilling <= 7 ? "text-amber-600" : "text-teal-600"}`}>
                              {t("clubDetails.next_billing_cycle", "Următoarea facturare (ciclu de 30 zile)")}
                            </p>
                            <p className={`text-xl font-bold ${daysUntilNextBilling <= 7 ? "text-amber-800" : "text-teal-800"}`}>
                              {nextBillingDate.toLocaleDateString("ro-RO", {
                                day: "numeric",
                                month: "long",
                                year: "numeric"
                              })}
                            </p>
                          </div>
                        </div>
                        <p className={`text-xs ${daysUntilNextBilling <= 7 ? "text-amber-600" : "text-teal-600"}`}>
                          {daysUntilNextBilling > 0
                            ? `${daysUntilNextBilling} ${t("clubDetails.days_remaining", "zile rămase")}`
                            : daysUntilNextBilling === 0
                              ? t("clubDetails.due_today", "Scadent azi")
                              : `${Math.abs(daysUntilNextBilling)} ${t("clubDetails.days_overdue", "zile întârziere")}`
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Billing Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {/* Next Billing Date */}
                  <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-6 border border-teal-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-white/80 rounded-xl shadow-sm">
                        <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-teal-600 font-medium">{t("clubDetails.next_billing_date", "Data următoarei facturări")}</p>
                        <p className="text-2xl font-bold text-teal-800">
                          {subscription?.current_period_end
                            ? new Date(subscription.current_period_end).toLocaleDateString("ro-RO", {
                                day: "numeric",
                                month: "long",
                                year: "numeric"
                              })
                            : t("clubDetails.no_subscription", "Fără abonament")
                          }
                        </p>
                      </div>
                    </div>
                    {subscription?.current_period_end && (
                      <p className="text-xs text-teal-600">
                        {(() => {
                          const nextBilling = new Date(subscription.current_period_end);
                          const today = new Date();
                          const daysUntil = Math.ceil((nextBilling.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                          return daysUntil > 0 ? `${daysUntil} ${t("clubDetails.days_remaining", "zile rămase")}` : t("clubDetails.billing_due", "Facturare scadentă");
                        })()}
                      </p>
                    )}
                  </div>

                  {/* Estimated Amount */}
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-6 border border-purple-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-white/80 rounded-xl shadow-sm">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-purple-600 font-medium">{t("clubDetails.estimated_next_invoice", "Următoarea factură estimată")}</p>
                        <p className="text-2xl font-bold text-purple-800">
                          €{((athletes.length * (club?.cost_per_athlete_cents || 100)) / 100).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-purple-600">
                      {athletes.length} {t("clubDetails.athletes_lower", "sportivi")} × €{((club?.cost_per_athlete_cents || 100) / 100).toFixed(2)}/{t("clubDetails.athlete", "sportiv")}
                    </p>
                  </div>

                  {/* Subscription Status */}
                  <div className={`rounded-2xl p-6 border ${
                    subscription?.status === "active"
                      ? "bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200"
                      : "bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200"
                  }`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-white/80 rounded-xl shadow-sm">
                        <svg className={`w-6 h-6 ${subscription?.status === "active" ? "text-emerald-600" : "text-amber-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${subscription?.status === "active" ? "text-emerald-600" : "text-amber-600"}`}>
                          {t("clubDetails.subscription_status", "Status abonament")}
                        </p>
                        <p className={`text-2xl font-bold capitalize ${subscription?.status === "active" ? "text-emerald-800" : "text-amber-800"}`}>
                          {subscription?.status || t("clubDetails.no_subscription", "Fără abonament")}
                        </p>
                      </div>
                    </div>
                    {subscription?.plan_code && (
                      <p className={`text-xs ${subscription?.status === "active" ? "text-emerald-600" : "text-amber-600"}`}>
                        {t("clubDetails.plan", "Plan")}: {subscription.plan_code}
                      </p>
                    )}
                  </div>
                </div>

                {/* Billing Timeline
                    NOTE: dropped `overflow-hidden` here — kept it only on
                    the gradient header below — so the per-bar hover
                    tooltip in the chart strip below can float above the
                    card's rounded corners without being clipped. */}
                {billingTimeline && (
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 mb-8">
                    {/* Timeline Header — keep the rounded top edge
                        clean by clipping just this strip, not the whole
                        card (otherwise the bar-chart tooltip below
                        couldn't escape). */}
                    <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-slate-200 rounded-t-2xl">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-lg shadow-sm">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800">{t("clubDetails.prorata_timeline", "Cronologie facturare pro-rata")}</h3>
                            <p className="text-sm text-slate-500">
                              {new Date(billingTimeline.month + "-01").toLocaleDateString("ro-RO", { month: "long", year: "numeric" })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">{t("clubDetails.total_until_today", "Total până azi")}</p>
                          <p className="text-2xl font-bold text-indigo-600">
                            €{(billingTimeline.totalUpToTodayCents / 100).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 grid grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-xs text-slate-500">{t("clubDetails.active_athletes", "Sportivi activi")}</p>
                        <p className="text-lg font-semibold text-slate-800">{billingTimeline.totalAthletes}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">{t("clubDetails.cost_athlete_month", "Cost/sportiv/lună")}</p>
                        <p className="text-lg font-semibold text-slate-800">€{(billingTimeline.costPerAthleteCents / 100).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">{t("clubDetails.cost_athlete_day", "Cost/sportiv/zi")}</p>
                        <p className="text-lg font-semibold text-slate-800">€{(billingTimeline.costPerAthletePerDayCents / 100).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">{t("clubDetails.projected_month_total", "Total lunar estimat")}</p>
                        <p className="text-lg font-semibold text-emerald-600">€{(billingTimeline.projectedMonthTotalCents / 100).toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Timeline Visualization
                        NOTE: previously the day-labels strip and the bar-
                        chart strip each had `overflow-x-auto`. The browser
                        forces overflow-y to a non-visible value whenever
                        overflow-x is auto/scroll, which clipped the hover
                        tooltip above each bar. We drop the per-strip
                        overflow and let the bars/labels share the parent's
                        natural width — on narrow viewports the bars
                        compress instead of scrolling, which is fine for
                        the 28–31 days a billing month contains. */}
                    <div className="px-6 py-4">
                      <div className="relative">
                        {/* Day labels */}
                        <div className="flex gap-0.5 mb-2 pb-2">
                          {billingTimeline.timeline.map((day) => (
                            <div
                              key={day.day}
                              className="flex-1 min-w-0 text-center"
                            >
                              <span className={`text-xs ${day.isToday ? "font-bold text-indigo-600" : "text-slate-400"}`}>
                                {day.day}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Bar chart */}
                        <div className="flex gap-0.5 items-end h-24 pb-2">
                          {billingTimeline.timeline.map((day) => {
                            const maxAmount = Math.max(...billingTimeline.timeline.map(d => d.dailyAmount));
                            const height = maxAmount > 0 ? (day.dailyAmount / maxAmount) * 100 : 0;
                            const hasJoinEvent = day.joinEvents.length > 0;

                            return (
                              <div
                                key={day.day}
                                className="flex-1 min-w-0 relative group"
                              >
                                {/* Bar */}
                                <div
                                  className={`w-full rounded-t transition-all ${
                                    day.isToday
                                      ? "bg-indigo-500"
                                      : day.isPast
                                        ? hasJoinEvent
                                          ? "bg-emerald-400"
                                          : "bg-slate-300"
                                        : "bg-slate-100"
                                  }`}
                                  style={{ height: `${Math.max(height, 4)}%` }}
                                />

                                {/* Join event indicator */}
                                {hasJoinEvent && (
                                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2">
                                    <div className="w-2 h-2 bg-emerald-500 rounded-full ring-2 ring-white" />
                                  </div>
                                )}

                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                                  <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                                    <p className="font-medium">{new Date(day.date).toLocaleDateString("ro-RO", { day: "numeric", month: "short" })}</p>
                                    <p>{day.athleteCount} {t("clubDetails.athletes_lower", "sportivi")}</p>
                                    <p>€{(day.dailyAmount / 100).toFixed(2)}/{t("clubDetails.day", "zi")}</p>
                                    {day.isPast || day.isToday ? (
                                      <p className="text-indigo-300">{t("clubDetails.cumulative", "Cumulativ")}: €{(day.cumulativeAmount / 100).toFixed(2)}</p>
                                    ) : null}
                                    {hasJoinEvent && (
                                      <div className="mt-1 pt-1 border-t border-slate-600">
                                        <p className="text-emerald-300 text-xs">{t("clubDetails.new_athletes", "Sportivi noi")}:</p>
                                        {day.joinEvents.map((evt, i) => (
                                          <p key={i} className="text-emerald-200">{evt.name}</p>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Legend */}
                        <div className="flex items-center justify-center gap-6 mt-4 text-xs text-slate-500">
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 bg-slate-300 rounded" />
                            <span>{t("clubDetails.past_days", "Zile trecute")}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 bg-indigo-500 rounded" />
                            <span>{t("clubDetails.today", "Azi")}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 bg-slate-100 rounded border border-slate-200" />
                            <span>{t("clubDetails.future", "Viitor")}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                            <span>{t("clubDetails.new_athlete_joined", "Sportiv nou înscris")}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Invoices Table */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                      </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.billing_history", "Istoric facturare")}</h2>
                  </div>
                  {isPlatformAdmin && (
                    <button
                      onClick={handleGenerateInvoice}
                      disabled={isGeneratingInvoice}
                      className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {isGeneratingInvoice ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          {t("clubDetails.generating", "Se generează...")}
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          {t("clubDetails.generate_invoice", "Generează factură")}
                        </>
                      )}
                    </button>
                  )}
                </div>
                {invoices.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="p-4 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                      </svg>
                    </div>
                    <p className="text-slate-600 font-medium mb-1">{t("clubDetails.no_invoices", "Nicio factură încă")}</p>
                    <p className="text-sm text-slate-500">{t("clubDetails.no_invoices_hint", "Istoricul facturilor va apărea aici după facturare")}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto bg-slate-50 rounded-xl border border-slate-200">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-100">
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.invoice_number", "Factură nr.")}</th>
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.date", "Dată")}</th>
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.amount", "Sumă")}</th>
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.status", "Status")}</th>
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.payment_link", "Link de plată")}</th>
                          <th className="text-left py-3 px-4 text-sm text-slate-500 font-medium">{t("clubDetails.actions", "Acțiuni")}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {invoices.map((invoice) => (
                          <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                            <td className="py-3 px-4 text-sm text-slate-800 font-mono">
                              {invoiceDocumentNumber(invoice)}
                              {!isFiscalInvoice(invoice) && (
                                <span className="ml-2 font-sans text-[11px] font-semibold text-amber-600">
                                  {invoiceDocumentLabel(invoice)}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600">
                              {formatDate(new Date(invoice.created_at))}
                            </td>
                            <td className="py-3 px-4 text-sm text-teal-600 font-medium">
                              €{((invoice.amount_cents || 0) / 100).toFixed(2)}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                invoice.paid_at
                                  ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                                  : "bg-amber-50 text-amber-600 border border-amber-200"
                              }`}>
                                {invoice.paid_at ? t("clubDetails.paid", "Plătit") : t("clubDetails.pending", "În așteptare")}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {invoice.stripe_payment_link ? (
                                <div className="flex items-center gap-2">
                                  <a
                                    href={invoice.stripe_payment_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-purple-600 hover:text-purple-700 hover:underline"
                                  >
                                    {t("clubDetails.open", "Deschide")}
                                  </a>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(invoice.stripe_payment_link);
                                      toast.success(t("clubDetails.link_copied", "Link de plată copiat!"));
                                    }}
                                    className="text-sm text-slate-500 hover:text-slate-700"
                                    title={t("clubDetails.copy_link", "Copiază linkul")}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                </div>
                              ) : (
                                <span className="text-sm text-slate-400">—</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <button
                                onClick={() => handleDownloadInvoicePdf(invoice.id)}
                                className="text-sm text-teal-600 hover:text-teal-700 hover:underline"
                              >
                                {t("clubDetails.download_pdf", "Descarcă PDF")}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === "logs" && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-800">{t("clubDetails.activity_logs", "Jurnal de activitate")}</h2>
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded-full text-xs font-medium">
                    {activityLogs.length} {t("clubDetails.events", "evenimente")}
                  </span>
                </div>

                {activityLogs.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="p-4 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <p className="text-slate-600 font-medium mb-1">{t("clubDetails.no_logs", "Niciun jurnal de activitate încă")}</p>
                    <p className="text-sm text-slate-500">{t("clubDetails.no_logs_hint", "Activitatea clubului va fi urmărită aici")}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activityLogs.map((log: any) => {
                      const getActionColor = (action: string) => {
                        if (action.includes("create") || action.includes("add")) return "bg-emerald-100 text-emerald-700 border-emerald-200";
                        if (action.includes("update") || action.includes("edit")) return "bg-blue-100 text-blue-700 border-blue-200";
                        if (action.includes("delete") || action.includes("remove")) return "bg-red-100 text-red-700 border-red-200";
                        return "bg-slate-100 text-slate-700 border-slate-200";
                      };

                      const getActionIcon = (action: string) => {
                        if (action.includes("create") || action.includes("add")) {
                          return (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          );
                        }
                        if (action.includes("update") || action.includes("edit")) {
                          return (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          );
                        }
                        if (action.includes("delete") || action.includes("remove")) {
                          return (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          );
                        }
                        return (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        );
                      };

                      const formatEntityType = (type: string) => {
                        const types: Record<string, string> = {
                          club: "Club",
                          team: "Echipă",
                          user_role: "Membru",
                          athlete: "Sportiv",
                          coach: "Antrenor",
                        };
                        return types[type] || type;
                      };

                      return (
                        <div
                          key={log.id}
                          className="p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-200 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${getActionColor(log.action)}`}>
                                {getActionIcon(log.action)}
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getActionColor(log.action)}`}>
                                    {log.action.replace(/_/g, " ")}
                                  </span>
                                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                                    {formatEntityType(log.entity_type)}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-600">
                                  {t("clubDetails.by", "de")} <span className="font-medium text-slate-800">{log.actor?.full_name || log.actor?.email || t("clubDetails.system", "Sistem")}</span>
                                </p>
                                {log.after && typeof log.after === "object" && (
                                  <div className="mt-2 text-xs text-slate-500">
                                    {log.after.name && <span>{t("clubDetails.name", "Nume")}: {log.after.name}</span>}
                                    {log.after.email && <span className="ml-2">{t("clubDetails.email", "Email")}: {log.after.email}</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-slate-500">
                                {new Date(log.created_at).toLocaleDateString("ro-RO", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })}
                              </p>
                              <p className="text-xs text-slate-400">
                                {new Date(log.created_at).toLocaleTimeString("ro-RO", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Add Member Modal */}
        {showAddMemberModal && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddMemberModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-teal-50 rounded-lg">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-slate-800">{t("clubDetails.add_member_to_club", "Adaugă membru în club")}</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.user_email", "Email utilizator")}</label>
                  <input
                    type="email"
                    value={newMemberEmail}
                    onChange={(e) => setNewMemberEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    disabled={isAddingMember}
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    {t("clubDetails.enter_existing_email", "Introduceți emailul utilizatorului existent în platformă")}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("clubDetails.role", "Rol")}</label>
                  <select
                    value={newMemberRole}
                    onChange={(e) => setNewMemberRole(e.target.value as "club_admin" | "coach" | "athlete")}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    disabled={isAddingMember}
                  >
                    <option value="athlete">{t("clubDetails.athlete", "Sportiv")}</option>
                    <option value="coach">{t("clubDetails.coach", "Antrenor")}</option>
                    <option value="club_admin">{t("clubDetails.club_admin", "Administrator club")}</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddMemberModal(false)}
                  disabled={isAddingMember}
                  className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors disabled:opacity-50"
                >
                  {t("clubDetails.cancel", "Anulează")}
                </button>
                <button
                  onClick={handleAddMember}
                  disabled={isAddingMember}
                  className="flex-1 px-4 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isAddingMember ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t("clubDetails.adding", "Se adaugă...")}
                    </>
                  ) : (
                    t("clubDetails.add_member", "Adaugă membru")
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Result Modal */}
        {showInvoiceResultModal && invoiceResult && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowInvoiceResultModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-slate-800">{t("clubDetails.invoice_generated_title", "Factură generată")}</h2>
              </div>

              <div className="space-y-4">
                {/* Invoice Details */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">{t("clubDetails.invoice_number", "Număr factură")}</span>
                    <span className="text-sm font-mono font-medium text-slate-800">
                      {invoiceResult.invoice ? invoiceDocumentNumber(invoiceResult.invoice) : "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">{t("clubDetails.amount", "Sumă")}</span>
                    <span className="text-sm font-medium text-teal-600">
                      €{((invoiceResult.invoice?.amount_cents || 0) / 100).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">{t("clubDetails.athletes", "Sportivi")}</span>
                    <span className="text-sm font-medium text-slate-800">
                      {invoiceResult.invoice?.athlete_count || 0}
                    </span>
                  </div>
                </div>

                {/* Payment Link */}
                {invoiceResult.payment_link && (
                  <div className="bg-purple-50 rounded-xl p-4">
                    <p className="text-sm font-medium text-purple-800 mb-2">{t("clubDetails.stripe_payment_link", "Link de plată Stripe")}</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={invoiceResult.payment_link}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-purple-200 rounded-lg text-xs font-mono text-slate-600"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(invoiceResult.payment_link!);
                          toast.success(t("clubDetails.link_copied", "Link copiat!"));
                        }}
                        className="px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-medium transition-colors"
                      >
                        {t("clubDetails.copy", "Copiază")}
                      </button>
                    </div>
                    <a
                      href={invoiceResult.payment_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      {t("clubDetails.open_payment_link", "Deschide linkul de plată")}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}

                {!invoiceResult.payment_link && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-sm text-amber-700">
                      {t("clubDetails.no_payment_link", "Linkul de plată nu a fost generat. Puteți descărca oricând factura PDF și o puteți partaja manual.")}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => handleDownloadInvoicePdf(invoiceResult.invoice?.id)}
                  className="flex-1 px-4 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {t("clubDetails.download_pdf", "Descarcă PDF")}
                </button>
                <button
                  onClick={() => setShowInvoiceResultModal(false)}
                  className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                >
                  {t("clubDetails.close", "Închide")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Activate: which plan, and what that costs */}
        {activateModalOpen && club && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="w-full max-w-lg bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-800">
                  {t("clubDetails.activate_title", "Activează clubul")}
                </h3>
                <button
                  onClick={() => setActivateModalOpen(false)}
                  className="p-2 rounded-full hover:bg-slate-100"
                  aria-label={t("clubDetails.close", "Închide")}
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-5 space-y-4">
                <p className="text-sm leading-relaxed text-slate-600">
                  {t(
                    "clubDetails.activate_body",
                    "Alege planul pe care intră clubul. Perioada demo se încheie, prețul pe sportiv se ia din plan și contorizarea pornește din acest moment."
                  )}
                </p>

                <div className="space-y-2">
                  {([
                    {
                      code: "basic" as const,
                      label: t("clubDetails.plan_basic", "Baza"),
                      hint: t("clubDetails.plan_basic_hint", "Tot ce are demo-ul, fără limită de timp."),
                    },
                    {
                      code: "premium" as const,
                      label: t("clubDetails.plan_premium", "Premium"),
                      hint: t(
                        "clubDetails.plan_premium_hint",
                        "În plus: cluburi multiple, branding propriu, notificări de antrenament și plată."
                      ),
                    },
                  ]).map((plan) => (
                    <label
                      key={plan.code}
                      className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                        activatePlan === plan.code
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="activate-plan"
                        value={plan.code}
                        checked={activatePlan === plan.code}
                        onChange={() => setActivatePlan(plan.code)}
                        className="mt-1 w-4 h-4 text-emerald-600 border-slate-300 focus:ring-emerald-500"
                      />
                      <span>
                        <span className="block text-sm font-bold text-slate-800">{plan.label}</span>
                        <span className="block mt-0.5 text-xs text-slate-500">{plan.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>

                <p className="text-xs text-slate-400">
                  {t(
                    "clubDetails.activate_note",
                    "Prețul pe sportiv se preia din Planuri. Dacă planul ales nu e configurat acolo, activarea este refuzată."
                  )}
                </p>
              </div>

              <div className="flex gap-3 px-5 py-4 border-t border-slate-100">
                <button
                  onClick={() => setActivateModalOpen(false)}
                  disabled={activating}
                  className="flex-1 px-4 py-3 font-medium transition-colors bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl disabled:opacity-60"
                >
                  {t("clubDetails.cancel", "Anulează")}
                </button>
                <button
                  onClick={handleActivateClub}
                  disabled={activating}
                  className="flex-1 px-4 py-3 font-bold text-white transition-colors bg-emerald-600 hover:bg-emerald-700 rounded-xl disabled:opacity-60"
                >
                  {activating
                    ? t("common.processing", "Se procesează...")
                    : t("clubDetails.activate_confirm", "Activează")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
